/**
 * Hook pour charger les métriques d'un chantier via RPC.
 * Migré vers React Query (useQuery + realtime invalidation).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';

export interface ChantierMetrics {
  budget_ht: number;
  date_debut: string | null;
  duree_estimee_jours: number;
  cout_journalier_equipe: number;
  cout_journalier_equipe_retard: number;
  cout_moyen_journalier?: number;     // Coût moyen journalier historique (mo_reel / jours_travailles_reels)
  jours_travailles_reels?: number;    // Jours ouvrés distincts avec au moins une affectation
  // Coûts de retard séparés (engagés vs prévisionnels)
  cout_retard_engage: number;        // Coûts déjà engagés (jours passés)
  cout_retard_previsionnel: number;  // Coûts futurs planifiés
  penalites_retard: number;          // Pénalités contractuelles de retard
  cout_retard: number;               // Total (engage + previsionnel + pénalités)
  cout_mo_total?: number;            // Coût total MO de toutes les affectations (comme onglet Équipe)
  cout_mo_previsionnel: number;      // Coût MO futur planifié (= cout_mo_total - cout_main_oeuvre_reel)
  couts_fixes_engages: number;
  jours_ecoules: number;
  cout_main_oeuvre_reel: number;     // Inclut heures sup si activées
  cout_mo_base?: number;             // MO sans heures sup (pour affichage séparé)
  marge_a_date: number;              // Utilise uniquement coûts engagés
  profitability_pct: number;
  budget_disponible: number;
  jour_critique: number | null;
  jours_restants_rentables: number | null;
  marge_finale: number;              // Utilise coûts totaux (engagés + prévisionnels)
  marge_finale_pct: number;
  statut_rentabilite: 'VERT' | 'JAUNE' | 'ORANGE' | 'ROUGE';
  // Heures supplémentaires
  cout_heures_sup?: number;          // Coût total des heures sup
  total_heures_sup?: number;         // Total des heures sup
  // Métriques de suspension
  est_suspendu?: boolean;
  date_suspension_actuelle?: string | null;
  nb_suspensions?: number;
  jours_suspension_total?: number;
  cout_suspension_total?: number;    // Coût des suspensions (mobilisation équipe)
  suspension_type?: string | null;
  suspension_raison?: string | null;
  suspension_date_reprise_estimee?: string | null;
  // Progression temporelle (frise)
  progress_pct?: number;     // Pourcentage de progression (0-100), calculé côté serveur
  jours_retard?: number;     // Jours de retard depuis affectations "Retard chantier"
  date_fin_effective?: string | null;  // MAX(date_fin) des affectations — date de fin réelle des travaux
}

export function useChantierMetrics(chantierId: string) {
  const { entrepriseId } = useSubscription();

  // -------------------------------------------------------------------------
  // Query : calcul des métriques via RPC
  // -------------------------------------------------------------------------
  const {
    data: metrics,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.chantiers.metrics(entrepriseId!, chantierId),
    queryFn: async () => {
      if (import.meta.env.DEV) console.log('[useChantierMetrics] 🔄 Computing metrics...');

      const { data: calcData, error: calcError } = await supabase
        .rpc('compute_chantier_metrics', { p_chantier: chantierId });

      if (calcError) throw calcError;

      if (import.meta.env.DEV) console.log('[useChantierMetrics] ✅ Computed metrics:', calcData);

      // Upsert dans la table cache (fire-and-forget pour ne pas bloquer le retour)
      supabase
        .from('chantier_metrics_realtime')
        .upsert({
          chantier_id: chantierId,
          metrics: calcData,
          updated_at: new Date().toISOString()
        })
        .then(({ error }) => {
          if (error && import.meta.env.DEV) console.error('[useChantierMetrics] ⚠️ Error upserting:', error);
        });

      return (calcData as unknown as ChantierMetrics) ?? null;
    },
    enabled: !!entrepriseId && !!chantierId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // -------------------------------------------------------------------------
  // API publique identique
  // -------------------------------------------------------------------------
  return {
    metrics: metrics ?? null,
    loading,
    error: queryError?.message ?? null,
    refresh: async () => { await refetch(); },
  };
}
